import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { queryClient } from 'merchant-api'
import { ProveMobileAuth } from 'merchant-components'
import { z } from 'zod'

const booleanSearchParam = z.preprocess((value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true';
  }
}, z.boolean());

export const Route = createFileRoute('/(public)/login/mobile/')({
  validateSearch: zodValidator(z.object({
    associate: booleanSearchParam.optional(),
    key: z.string().trim(),
    flowType: z.enum(['desktop', 'mobile']).default('desktop'),
    phoneMasked: z.string().trim().optional(),
    redirect: z.string().trim().optional(),
  })),
  component: () => {
    const { associate, key, flowType, phoneMasked, redirect } = Route.useSearch()
    const navigate = useNavigate();
    const authToken = readProveAuthToken(key);
    const phoneNumber = readProvePhoneNumber(key);

    return (
      <ProveMobileAuth
        associate={associate === true}
        OTPKey={key}
        authToken={authToken}
        flowType={flowType}
        phoneNumber={phoneNumber}
        phoneMasked={phoneMasked}
        onContinue={() => {
          clearProveSessionData(key);
          navigate({ to: redirect || '/' });
        }}
        onFallback={(data) => {
          clearProveSessionData(key);
          navigate({
            to: '/login/otp',
            search: {
              emailMasked: data.emailMasked,
              key: data.key,
              redirect: redirect || '/',
              verifyEmailAddressTitle: associate === true,
            },
          });
        }}
        onVerify={async (data) => {
          clearProveSessionData(key);
          // The verify minted a new session: drop every cached query so nothing from the
          // previous identity leaks into this one.
          queryClient.clear();
          if (data.appId) return navigate({ to: '/application/$id', params: { id: data.appId } });

          navigate({ to: redirect || '/' });
        }}
      />
    )
  },
})

function readProveAuthToken(key: string) {
  try {
    return window.sessionStorage.getItem(`proveAuthToken:${key}`) ?? undefined;
  } catch {
    return undefined;
  }
}

function readProvePhoneNumber(key: string) {
  try {
    return window.sessionStorage.getItem(`provePhoneNumber:${key}`) ?? undefined;
  } catch {
    return undefined;
  }
}

function clearProveSessionData(key: string) {
  try {
    window.sessionStorage.removeItem(`proveAuthToken:${key}`);
    window.sessionStorage.removeItem(`provePhoneNumber:${key}`);
  } catch {
    // The session can still complete if cleanup is blocked.
  }
}
